<template>
    <Transition name="modal">
        <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center">
            <!-- Backdrop -->
            <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" @click="closeModal"></div>

            <!-- Modal Content -->
            <div
                class="relative bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
                <!-- Header -->
                <div class="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                    <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-200">
                        {{ title || 'Video Player' }}
                    </h3>
                    <button @click="closeModal"
                        class="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                        <i class="fas fa-times text-slate-600 dark:text-slate-400"></i>
                    </button>
                </div>

                <!-- Video Container -->
                <div class="relative video-container">
                    <video ref="videoPlayer" :src="videoSrc" class="w-full h-auto max-h-[70vh] object-contain" controls
                        preload="metadata" @loadedmetadata="onVideoLoaded" @error="onVideoError">
                        Your browser does not support the video tag.
                    </video>

                    <!-- Loading State -->
                    <div v-if="isLoading"
                        class="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                        <div class="flex items-center space-x-2">
                            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                            <span class="text-slate-600 dark:text-slate-400">Loading video...</span>
                        </div>
                    </div>

                    <!-- Error State -->
                    <div v-if="hasError"
                        class="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                        <div class="text-center">
                            <i class="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
                            <p class="text-slate-600 dark:text-slate-400">Failed to load video</p>
                            <button @click="retryLoad"
                                class="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                                Retry
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div v-if="description" class="p-4 border-t border-slate-200 dark:border-slate-700">
                    <p class="text-slate-600 dark:text-slate-400 text-sm">{{ description }}</p>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script>
import { ref, watch, nextTick } from 'vue'

export default {
    name: 'VideoModal',
    props: {
        isOpen: {
            type: Boolean,
            default: false
        },
        videoSrc: {
            type: String,
            required: true
        },
        title: {
            type: String,
            default: ''
        },
        description: {
            type: String,
            default: ''
        }
    },
    emits: ['close'],
    setup(props, { emit }) {
        const videoPlayer = ref(null)
        const isLoading = ref(false)
        const hasError = ref(false)

        const closeModal = () => {
            console.log('closeModal called') // Debug log
            if (videoPlayer.value) {
                videoPlayer.value.pause()
            }
            emit('close')
        }

        const onVideoLoaded = () => {
            isLoading.value = false
            hasError.value = false
        }

        const onVideoError = () => {
            isLoading.value = false
            hasError.value = true
        }

        const retryLoad = () => {
            hasError.value = false
            isLoading.value = true
            if (videoPlayer.value) {
                videoPlayer.value.load()
            }
        }

        // Handle escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape' && props.isOpen) {
                closeModal()
            }
        }

        // Watch for modal open/close
        watch(() => props.isOpen, (newVal) => {
            if (newVal) {
                isLoading.value = true
                hasError.value = false
                nextTick(() => {
                    if (videoPlayer.value) {
                        videoPlayer.value.load()
                    }
                })
                document.addEventListener('keydown', handleEscape)
                document.body.style.overflow = 'hidden'
            } else {
                document.removeEventListener('keydown', handleEscape)
                document.body.style.overflow = ''
            }
        })

        return {
            videoPlayer,
            isLoading,
            hasError,
            closeModal,
            onVideoLoaded,
            onVideoError,
            retryLoad
        }
    }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: all 0.3s ease;
}

.modal-enter-from {
    opacity: 0;
    transform: scale(0.9);
}

.modal-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

/* Video styling - memastikan video tidak terpengaruh oleh styling yang membuat gelap */
video {
    background: transparent !important;
    filter: none !important;
    -webkit-filter: none !important;
    opacity: 1 !important;
    mix-blend-mode: normal !important;
}

/* Memastikan video content tidak terpengaruh */
video::-webkit-media-controls-enclosure {
    background: transparent !important;
}

/* Custom video controls styling - hanya untuk controls, bukan video content */
video::-webkit-media-controls-panel {
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7)) !important;
}

video::-webkit-media-controls-play-button {
    background-color: rgba(255, 255, 255, 0.9) !important;
    border-radius: 50% !important;
    color: #000 !important;
}

video::-webkit-media-controls-timeline {
    background-color: rgba(255, 255, 255, 0.3) !important;
}

video::-webkit-media-controls-current-time-display,
video::-webkit-media-controls-time-remaining-display {
    color: white !important;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8) !important;
}

video::-webkit-media-controls-volume-slider {
    background-color: rgba(255, 255, 255, 0.3) !important;
}

video::-webkit-media-controls-mute-button {
    background-color: rgba(255, 255, 255, 0.9) !important;
    border-radius: 50% !important;
    color: #000 !important;
}

/* Firefox video controls */
video::-moz-media-controls-panel {
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7)) !important;
}

/* Edge video controls */
video::-ms-media-controls-panel {
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7)) !important;
}

/* Memastikan video container tidak mempengaruhi video */
.video-container {
    background: transparent !important;
    filter: none !important;
}

/* Override any potential dark mode effects on video */
.dark video {
    background: transparent !important;
    filter: none !important;
    -webkit-filter: none !important;
    opacity: 1 !important;
    mix-blend-mode: normal !important;
}
</style>